import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { BLUEPRINT_CHECKOUT_URL, withUTM } from "@/config/blueprint";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/blueprint", label: "The Program" },
  { to: "/included", label: "What's Included" },
  { to: "/hands-on-training", label: "Hands-On Training" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

// Blueprint-specific anchor nav — jumps within the sales page instead of
// exposing the full-site nav on that route.
const BLUEPRINT_NAV = [
  { href: "#what-youll-learn", label: "What You'll Learn", target: "[data-testid=\"blueprint-page\"] section:nth-of-type(2)" },
  { href: "#curriculum", label: "Curriculum" },
  { href: "#whats-included", label: "What's Included" },
  { href: "#faq", label: "FAQ" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isBlueprint = pathname === "/blueprint";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [pathname]);

  const bgClass = scrolled || open
    ? "bg-black/96 backdrop-blur-md border-b border-[rgba(217,164,65,0.22)]"
    : "bg-gradient-to-b from-black/50 to-transparent border-b border-transparent";

  const scrollToAnchor = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const target = document.querySelector(href);
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.location.hash = href;
  };

  return (
    <header data-testid="site-header" className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${bgClass}`}>
      <div className="bcu-container-wide flex items-center justify-between h-24 md:h-28">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3 shrink-0">
          <img src="/assets/bcu_logo_official.png" alt="Bounce City University" className="h-16 md:h-20 w-auto drop-shadow-[0_6px_16px_rgba(0,0,0,0.6)]" />
        </Link>

        {/* Nav — Blueprint sales page gets a compact anchor nav, all other pages keep the site nav */}
        <nav className="hidden xl:flex items-center gap-8">
          {isBlueprint ? (
            BLUEPRINT_NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={(e) => scrollToAnchor(e, n.href)}
                data-testid={`bp-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="font-body text-[13px] font-semibold tracking-[0.16em] uppercase text-white hover:text-[#F7931E] transition-colors py-1"
              >
                {n.label}
              </a>
            ))
          ) : (
            NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={({ isActive }) =>
                  `font-body text-[13px] font-semibold tracking-[0.16em] uppercase transition-colors relative py-1 ${
                    isActive ? "text-[#F7931E] border-b border-[#F7931E] pb-1" : "text-white hover:text-[#F7931E]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))
          )}
        </nav>

        <div className="hidden xl:flex">
          {isBlueprint ? (
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("bcu:open-blueprint-modal", { detail: { placement: "header" } }))}
              data-testid="header-blueprint-cta"
              className="bcu-btn-primary text-[13px] tracking-[0.14em] uppercase px-5 py-2.5"
            >
              Get Access — $497 <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <Link to="/apply" data-testid="header-apply-btn" className="bcu-btn-primary text-[13px] tracking-[0.14em] uppercase px-5 py-2.5">
              Apply Now <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="xl:hidden inline-flex items-center justify-center h-11 w-11 rounded border border-[rgba(255,255,255,0.20)] text-white"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden bg-black border-t border-[rgba(217,164,65,0.22)]" data-testid="mobile-menu">
          <div className="bcu-container py-8 flex flex-col gap-4">
            {isBlueprint ? (
              <>
                {BLUEPRINT_NAV.map((n) => (
                  <a
                    key={n.href}
                    href={n.href}
                    onClick={(e) => scrollToAnchor(e, n.href)}
                    data-testid={`mobile-bp-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="font-body text-lg font-semibold tracking-[0.10em] uppercase text-white"
                  >
                    {n.label}
                  </a>
                ))}
                <button
                  type="button"
                  onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent("bcu:open-blueprint-modal", { detail: { placement: "header_mobile" } })); }}
                  data-testid="mobile-blueprint-cta"
                  className="bcu-btn-primary mt-2 justify-center"
                >
                  Get Access — $497 <ArrowRight className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={({ isActive }) =>
                      `font-body text-lg font-semibold tracking-[0.10em] uppercase ${
                        isActive ? "text-[#F7931E]" : "text-white"
                      }`
                    }
                  >
                    {n.label}
                  </NavLink>
                ))}
                <Link to="/apply" data-testid="mobile-apply-btn" className="bcu-btn-primary mt-2 justify-center">
                  Apply Now <ArrowRight className="h-4 w-4" />
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
